import {
  ComponentPropsWithoutRef,
  useState,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { DateTime, type WeekdayNumbers } from "luxon";
import cx from "classnames";
import styles from "./Calendar.module.scss";

import {
  CalendarPrevButton,
  CalendarNextButton,
  CalendarMonthButton,
  CalendarYearButton,
  CalendarNowButton,
} from "./header-buttons";

import { CalendarMonth } from "./CalendarMonth";

import { IanaZone, Locale, LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import { normalizeDate } from "./utils";
import { CalendarMonthSelection } from "./CalendarMonthSelection";
import { CalendarYearSelection } from "./CalendarYearSelection";
import {
  CalendarContext,
  CalendarContextProps,
  CalendarRangeContextProps,
} from "./CalendarContext";

export type CalendarValue = string | undefined;

export type CalendarRangeValue = { start?: string; end?: string } | undefined;

/**
 * Props for the Calendar component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "value" | "defaultValue">
 * @extends LayoutUtilProps
 */
type CalendarCommonProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "value" | "defaultValue"
> & {
  /**
   * @deprecated children will be overridden internally
   */
  children?: ComponentPropsWithoutRef<"div">["children"];
  /**
   * The default focused date.
   */
  defaultFocusedDate?: string | null;
  /**
   * The focused date. Used to determine what month and year is currently selected.
   */
  focusedDate?: string | null;
  /**
   * The minimum date allowed.
   */
  minDate?: string;
  /**
   * The maximum date allowed.
   */
  maxDate?: string;
  /**
   * @deprecated use unavailable.dates instead
   */
  unavailableDates?: string[];
  /**
   * The days of the week and dates that are unavailable.
   */
  unavailable?: {
    /**
     * The days of the week that are unavailable.
     */
    daysOfWeek?: WeekdayNumbers[];
    /**
     * The specific dates that are unavailable.
     */
    dates?: string[];
  };
  /**
   * The locale to use for the calendar.
   */
  locale?: Locale;
  /**
   * The time zone to use for the calendar.
   */
  defaultTimeZone?: IanaZone;
  /**
   * The day of the week to start the calendar on.
   */
  startDay?: "Sunday" | "Monday";

  /**
   * Whether the calendar should focus on the focused date.
   * @internal - Do not use this prop directly.
   */
  _disableAutofocus?: boolean;
};

type CalendarSingleProps = CalendarCommonProps & {
  /**
   * The function to call when the value changes.
   *
   * @param data - The data to pass to the function.
   */
  onSelection?: (data: { value: CalendarValue }) => void;
  /**
   * The default date value for uncontrolled mode.
   */
  defaultValue?: string | false;
  /**
   * The value for controlled mode.
   */
  value?: string;
  /**
   * Whether the calendar is in range mode.
   */
  range?: false;
};

type CalendarRangeProps = CalendarCommonProps & {
  /**
   * The function to call when the value changes.
   *
   * @param data.value - The date value that is selected.
   */
  onSelection?: (data: { value: CalendarRangeValue }) => void;
  /**
   * The default date value for uncontrolled mode.
   */
  defaultValue?: { start?: string; end?: string };
  /**
   * The date value for controlled mode.
   */
  value?: { start?: string; end?: string };
  /**
   * Whether the calendar is in range mode.
   */
  range: true;
};

export type CalendarProps = LayoutUtilProps &
  (CalendarSingleProps | CalendarRangeProps);

/**
 * Calendar component for viewing and selecting a date or range of dates.
 *
 * Features:
 * - Single date and date range selection modes
 * - Month and year navigation with header buttons
 * - Configurable start day (Sunday or Monday)
 * - Date range restrictions with min/max dates
 * - Unavailable dates and days of week support
 * - Locale and timezone customization
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Responsive design with automatic width detection
 * - Month and year selection views
 * - Today button for quick navigation
 *
 * @example
 * <Calendar
 *   value="2024-01-15"
 *   onSelection={({ value }) => console.log('Selected:', value)}
 *   minDate="2024-01-01"
 *   maxDate="2024-12-31"
 * />
 */
export const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  (props, ref) => {
    const uid = useId();
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- overriding children internally
      children,
      focusedDate: focusProp,
      defaultFocusedDate,
      locale = Intl.DateTimeFormat().resolvedOptions().locale as Locale,
      defaultTimeZone = Intl.DateTimeFormat().resolvedOptions()
        .timeZone as IanaZone,
      startDay = "Sunday",
      minDate,
      maxDate,
      unavailableDates = [],
      unavailable = {
        dates: [],
        daysOfWeek: [],
      },
      id,
      style,
      value: iValue,
      defaultValue: iDefaultValue,
      onSelection,
      range,
      className,
      _disableAutofocus,
      ...remainingProps
    } = componentProps;

    const calendarRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([ref, calendarRef]);
    const lastIValue = useRef(iValue);
    const buttonsRef = useRef<NodeListOf<Element> | undefined>(undefined);

    const [calendarWidth, setCalendarWidth] = useState<number | undefined>(
      undefined,
    );

    const [calendarSelectionState, setCalendarSelectionState] = useState<
      "day" | "month" | "year"
    >("day");

    useEffect(() => {
      if (!calendarRef.current || style?.width != null) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (calendarSelectionState === "day" && width > 100) {
            setCalendarWidth(width);
          }
        }
      });

      resizeObserver.observe(calendarRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [calendarSelectionState, style?.width]);

    // Common
    const styleCombined = {
      width: calendarWidth,
      ...style,
      ...layoutStyles,
    };

    const classNameCombined = cx(styles["calendar"], className);

    const { nDate, nDefaultDate, nToday } = useMemo(
      () => ({
        nDate: iValue
          ? typeof iValue === "string"
            ? normalizeDate(iValue, defaultTimeZone)
            : {
                start: iValue.start
                  ? normalizeDate(iValue.start, defaultTimeZone)
                  : null,
                end: iValue.end
                  ? normalizeDate(iValue.end, defaultTimeZone)
                  : null,
              }
          : null,
        nDefaultDate: iDefaultValue
          ? typeof iDefaultValue === "string"
            ? normalizeDate(iDefaultValue, defaultTimeZone)
            : {
                start: iDefaultValue.start
                  ? normalizeDate(iDefaultValue.start, defaultTimeZone)
                  : null,
                end: iDefaultValue?.end
                  ? normalizeDate(iDefaultValue.end, defaultTimeZone)
                  : null,
              }
          : null,
        nToday:
          typeof iValue === "string"
            ? normalizeDate(DateTime.now().toISO(), defaultTimeZone)
            : {
                start: normalizeDate(DateTime.now().toISO(), defaultTimeZone),
                end: normalizeDate(DateTime.now().toISO(), defaultTimeZone),
              },
      }),
      [iValue, iDefaultValue, defaultTimeZone],
    );

    const [value, setValue] = useState<
      | CalendarContextProps["value"]
      | CalendarRangeContextProps["value"]
      | undefined
    >(nDate ?? nDefaultDate ?? nToday);

    const getInitialDate = useMemo(() => {
      if (defaultFocusedDate) {
        return normalizeDate(defaultFocusedDate, defaultTimeZone);
      }
      if (iDefaultValue) {
        const date =
          typeof iDefaultValue === "string"
            ? normalizeDate(iDefaultValue, defaultTimeZone)
            : normalizeDate(
                iDefaultValue.start ?? iDefaultValue.end ?? "",
                defaultTimeZone,
              );
        return date;
      }
      if (
        !DateTime.isDateTime(nDate) &&
        !DateTime.isDateTime(nDefaultDate) &&
        !DateTime.isDateTime(nToday)
      ) {
        return nDate?.start ?? nDefaultDate?.start ?? nToday?.start;
      }

      return (nDate ?? nDefaultDate ?? nToday) as DateTime;
    }, [
      defaultFocusedDate,
      iDefaultValue,
      nDate,
      nDefaultDate,
      nToday,
      defaultTimeZone,
    ]);

    const initialDate = getInitialDate ?? DateTime.now();

    const [focusedDate, setFocusedDate] = useState<
      | CalendarContextProps["focusedDate"]
      | CalendarRangeContextProps["focusedDate"]
      | undefined
    >(initialDate);

    const [hoveredDate, setHoveredDate] = useState<
      | CalendarContextProps["hoveredDate"]
      | CalendarRangeContextProps["hoveredDate"]
    >();

    const [selectedMonth, setSelectedMonth] = useState<DateTime["month"]>(
      initialDate.month,
    );
    const [selectedYear, setSelectedYear] = useState<DateTime["year"]>(
      initialDate.year,
    );

    useEffect(() => {
      if (!focusProp && !defaultFocusedDate) return;
      if (focusProp) {
        setFocusedDate(normalizeDate(focusProp, defaultTimeZone));
      } else if (defaultFocusedDate) {
        setFocusedDate(normalizeDate(defaultFocusedDate, defaultTimeZone));
        setSelectedMonth(
          normalizeDate(defaultFocusedDate, defaultTimeZone).month,
        );
        setSelectedYear(
          normalizeDate(defaultFocusedDate, defaultTimeZone).year,
        );
      }
      // defaultTimeZone should not rerender
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusProp, defaultFocusedDate]);

    useEffect(() => {
      if (!iValue) {
        lastIValue.current = iValue;
        return;
      }
      if (typeof iValue === "string") {
        setValue(normalizeDate(iValue, defaultTimeZone));
        setFocusedDate(normalizeDate(iValue, defaultTimeZone));
      } else if (
        iValue.end &&
        (typeof lastIValue.current !== "object" ||
          lastIValue?.current?.end !== iValue.end)
      ) {
        setValue({
          start: iValue.start
            ? normalizeDate(iValue.start, defaultTimeZone)
            : null,
          end: iValue.end ? normalizeDate(iValue.end, defaultTimeZone) : null,
        });
        setFocusedDate(normalizeDate(iValue.end, defaultTimeZone));
      } else if (
        iValue.start &&
        (typeof lastIValue.current !== "object" ||
          lastIValue?.current?.start !== iValue.start)
      ) {
        setFocusedDate(normalizeDate(iValue.start, defaultTimeZone));
      }
      lastIValue.current = iValue;
      // defaultTimeZone should not rerender
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iValue]);

    // Memoize normalized minDate and maxDate to avoid new references on every render
    const normalizedMinDate = useMemo(
      () => (minDate ? normalizeDate(minDate, defaultTimeZone) : null),
      [minDate, defaultTimeZone],
    );
    const normalizedMaxDate = useMemo(
      () => (maxDate ? normalizeDate(maxDate, defaultTimeZone) : null),
      [maxDate, defaultTimeZone],
    );

    const providerValue = useMemo(() => {
      // TODO FUTURE: Hookup with dateformat from localization provider
      const format = {
        weekday: "long",
        month: "long",
        day: "2-digit",
        year: "numeric",
      } as const;

      return {
        disableAutofocus: _disableAutofocus ?? false,
        focusedDate,
        setFocusedDate,
        selectedMonth,
        setSelectedMonth,
        selectedYear,
        setSelectedYear,
        hoveredDate,
        setHoveredDate,
        value,
        setValue,
        defaultTimeZone,
        id: id ?? uid,
        onSelection,
        controlled: !!iValue,
        range,
        minDate: normalizedMinDate,
        maxDate: normalizedMaxDate,
        unavailableDates: new Set(unavailableDates),
        unavailable: {
          dates: new Set(unavailable.dates),
          daysOfWeek: new Set(unavailable.daysOfWeek),
        },
        locale,
        dateFormat: format,
        startDay,
      };
    }, [
      focusedDate,
      selectedMonth,
      selectedYear,
      hoveredDate,
      id,
      locale,
      normalizedMaxDate,
      normalizedMinDate,
      iValue,
      onSelection,
      range,
      defaultTimeZone,
      uid,
      unavailableDates,
      unavailable,
      value,
      startDay,
    ]);

    // Cache header button query
    const updateHeaderButtons = useCallback(() => {
      buttonsRef.current = calendarRef.current?.querySelectorAll(
        "[data-calendar-header-button]",
      );
    }, []);

    useEffect(() => {
      updateHeaderButtons();
    }, [updateHeaderButtons]);

    const handleHeaderKeyDown = useCallback((e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (!buttonsRef.current || buttonsRef.current.length === 0) {
        return;
      }

      const buttonArray = Array.from(buttonsRef.current);
      const currentIndex = buttonArray.indexOf(target as HTMLButtonElement);

      if (currentIndex === -1) return;

      let newIndex: number;
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          newIndex =
            currentIndex > 0 ? currentIndex - 1 : buttonArray.length - 1;
          (buttonArray[newIndex] as HTMLButtonElement).focus();
          break;
        case "ArrowRight":
          e.preventDefault();
          newIndex =
            currentIndex < buttonArray.length - 1 ? currentIndex + 1 : 0;
          (buttonArray[newIndex] as HTMLButtonElement).focus();
          break;
      }
    }, []);

    const handleMonthSelection = useCallback(
      (month: DateTime["month"]) => {
        setSelectedMonth(month);
        setFocusedDate(
          DateTime.fromObject({
            year: focusedDate?.year ?? 1,
            month: month,
            day: 1,
          }),
        );
        setCalendarSelectionState("day");
      },
      [focusedDate],
    );

    const handleYearSelection = useCallback(
      (year: DateTime["year"]) => {
        setSelectedYear(year);
        setFocusedDate(
          DateTime.fromObject({
            year: year,
            month: focusedDate?.month ?? 1,
            day: 1,
          }),
        );
        setCalendarSelectionState("day");
      },
      [focusedDate],
    );

    return (
      <CalendarContext.Provider value={providerValue as CalendarContextProps}>
        <div
          ref={combinedRef}
          id={id ?? uid}
          style={styleCombined}
          className={classNameCombined}
          data-anv="calendar"
          {...remainingProps}
        >
          <div className={styles["header"]}>
            <span
              aria-label="Calendar navigation"
              role="toolbar"
              onKeyDown={handleHeaderKeyDown}
              className={styles["controller"]}
            >
              <CalendarMonthButton
                data-calendar-header-button
                calendarSelectionState={calendarSelectionState}
                onClick={() =>
                  setCalendarSelectionState((prev) =>
                    prev === "month" ? "day" : "month",
                  )
                }
              />
              <CalendarYearButton
                tabIndex={-1}
                data-calendar-header-button
                calendarSelectionState={calendarSelectionState}
                onClick={() =>
                  setCalendarSelectionState((prev) =>
                    prev === "year" ? "day" : "year",
                  )
                }
              />
              {calendarSelectionState === "day" && (
                <>
                  <CalendarPrevButton
                    tabIndex={-1}
                    data-calendar-header-button
                  />
                  <CalendarNextButton
                    tabIndex={-1}
                    data-calendar-header-button
                  />
                  <CalendarNowButton
                    tabIndex={-1}
                    data-calendar-header-button
                  />
                </>
              )}
            </span>
          </div>
          {calendarSelectionState === "day" && <CalendarMonth />}
          {calendarSelectionState === "month" && (
            <CalendarMonthSelection onMonthSelection={handleMonthSelection} />
          )}
          {calendarSelectionState === "year" && (
            <CalendarYearSelection onYearSelection={handleYearSelection} />
          )}
        </div>
      </CalendarContext.Provider>
    );
  },
);

Calendar.displayName = "Calendar";
